import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { BekraftaModal } from '../../components/BekraftaModal'

const ALLA_AVDELNINGAR = ['RN', 'LN MMÖ', 'LN NKP', 'DISP']
const TOM_FORM = { namn: '', starttid: '', sluttid: '', har_lunchrast: false, lunchlangd_min: 30, kommentar: '', avdelningar: [] }

export function PasstyperPage() {
  const [passtyper, setPasstyper] = useState([])
  const [visaModal, setVisaModal] = useState(false)
  const [redigerar, setRedigerar] = useState(null) // passtyp-objekt, eller null vid "ny"
  const [sparar, setSparar] = useState(false)
  const [form, setForm] = useState(TOM_FORM)
  const [taBortDialog, setTaBortDialog] = useState(null)
  const overlayMouseDownRef = useRef(false)

  useEffect(() => { hamtaPasstyper() }, [])

  async function hamtaPasstyper() {
    const { data } = await supabase.from('passtyper').select('*').order('namn')
    setPasstyper(data || [])
  }

  function raknaTimmar() {
    if (!form.starttid || !form.sluttid) return 0
    const [sh, sm] = form.starttid.split(':').map(Number)
    const [eh, em] = form.sluttid.split(':').map(Number)
    let min = (eh * 60 + em) - (sh * 60 + sm)
    if (min < 0) min += 24 * 60
    if (form.har_lunchrast) min -= form.lunchlangd_min
    return parseFloat((min / 60).toFixed(2))
  }

  function oppnaNy() {
    setRedigerar(null)
    setForm(TOM_FORM)
    setVisaModal(true)
  }

  function oppnaRedigera(p) {
    setRedigerar(p)
    setForm({
      namn: p.namn || '',
      starttid: p.starttid || '',
      sluttid: p.sluttid || '',
      har_lunchrast: p.har_lunchrast || false,
      lunchlangd_min: p.lunchlangd_min || 30,
      kommentar: p.kommentar || '',
      avdelningar: p.avdelningar || []
    })
    setVisaModal(true)
  }

  function vaxlaAvdelning(avd) {
    setForm(f => ({
      ...f,
      avdelningar: f.avdelningar.includes(avd)
        ? f.avdelningar.filter(a => a !== avd)
        : [...f.avdelningar, avd]
    }))
  }

  async function handleSpara() {
    setSparar(true)
    try {
      const arbetstimmar = raknaTimmar()
      const data = {
        ...form,
        namn: form.namn.toUpperCase(),
        arbetstimmar,
        lunchlangd_min: form.har_lunchrast ? form.lunchlangd_min : 0,
        kommentar: form.kommentar || null,
      }
      if (redigerar) {
        await supabase.from('passtyper').update(data).eq('id', redigerar.id)
      } else {
        await supabase.from('passtyper').insert(data)
      }
      setVisaModal(false)
      setRedigerar(null)
      setForm(TOM_FORM)
      hamtaPasstyper()
    } catch (err) {
      alert('Fel: ' + err.message)
    } finally {
      setSparar(false)
    }
  }

  async function bekraftaTaBort() {
    const id = taBortDialog.id
    setTaBortDialog(null)
    await supabase.from('passtyper').delete().eq('id', id)
    hamtaPasstyper()
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      <div className="content-header">
        <div>
          <h1>Passtyper</h1>
          <div className="content-sub">Hantera befintliga och lägg till nya pass</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={oppnaNy}>+ Ny passtyp</button>
      </div>

      <div className="content-body">
        <div className="card">
          {passtyper.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 6, border: '1px solid #ececec', marginBottom: 7, background: '#fff' }}>
              <div style={{ width: 36, height: 32, borderRadius: 4, background: '#1a1a1a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '.78rem', flexShrink: 0 }}>
                {p.namn}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '.88rem' }}>{p.namn}</div>
                <div style={{ fontSize: '.75rem', color: '#aaa', marginTop: 2 }}>
                  {p.starttid}–{p.sluttid}
                  {p.ar_x_dag ? ' · Balanseras automatiskt' : ` · ${p.arbetstimmar} h`}
                  {p.har_lunchrast ? ' · 30 min lunchrast' : p.arbetstimmar > 0 ? ' · ingen lunchrast' : ''}
                </div>
                {p.kommentar && (
                  <div style={{ fontSize: '.75rem', color: '#888', marginTop: 2, fontStyle: 'italic' }}>
                    {p.kommentar}
                  </div>
                )}
                {p.avdelningar && p.avdelningar.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                    {p.avdelningar.map(a => (
                      <span key={a} style={{ fontSize: '.66rem', background: '#f0f0f0', color: '#666', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>{a}</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexShrink: 0 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => oppnaRedigera(p)}>✏️</button>
                {!p.ar_x_dag && (
                  <button className="btn btn-danger btn-sm" onClick={() => setTaBortDialog(p)}>✕</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {visaModal && (
        <div
          className="modal-overlay"
          onMouseDown={e => { overlayMouseDownRef.current = (e.target === e.currentTarget) }}
          onClick={e => { if (e.target === e.currentTarget && overlayMouseDownRef.current) setVisaModal(false) }}
        >
          <div className="modal-box">
            <div className="modal-header">
              <h3>⚙️ {redigerar ? 'Redigera passtyp' : 'Ny passtyp'}</h3>
              <button className="modal-close" onClick={() => setVisaModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <label className="form-label">Passnamn (t.ex. D4, F2)</label>
              <input className="form-control" placeholder="D4" value={form.namn} onChange={e => setForm({...form, namn: e.target.value})} />
              <div className="form-row">
                <div>
                  <label className="form-label">Starttid</label>
                  <input className="form-control" type="time" value={form.starttid} onChange={e => setForm({...form, starttid: e.target.value})} />
                </div>
                <div>
                  <label className="form-label">Sluttid</label>
                  <input className="form-control" type="time" value={form.sluttid} onChange={e => setForm({...form, sluttid: e.target.value})} />
                </div>
              </div>
              {form.starttid && form.sluttid && (
                <div className="info-box info-box-blue" style={{ marginTop: -6 }}>
                  Beräknade arbetstimmar: <strong>{raknaTimmar()} h</strong>
                </div>
              )}
              <div className="checkbox-row">
                <input type="checkbox" checked={form.har_lunchrast} onChange={e => setForm({...form, har_lunchrast: e.target.checked})} id="lunch" />
                <label htmlFor="lunch" style={{ fontWeight: 500, fontSize: '.86rem', cursor: 'pointer' }}>Inkluderar lunchrast</label>
              </div>
              {form.har_lunchrast && (
                <>
                  <label className="form-label">Lunchlängd (minuter)</label>
                  <input className="form-control" type="number" value={form.lunchlangd_min} onChange={e => setForm({...form, lunchlangd_min: parseInt(e.target.value)})} />
                </>
              )}

              <label className="form-label">Kommentar (visas i kursiv i listan)</label>
              <input className="form-control" placeholder="T.ex. operativt, backup för DC..." value={form.kommentar} onChange={e => setForm({...form, kommentar: e.target.value})} />

              <label className="form-label">Avdelningar som använder passet</label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '4px 0 12px' }}>
                {ALLA_AVDELNINGAR.map(avd => (
                  <div key={avd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" id={`avd-${avd}`} checked={form.avdelningar.includes(avd)} onChange={() => vaxlaAvdelning(avd)} />
                    <label htmlFor={`avd-${avd}`} style={{ fontSize: '.84rem', cursor: 'pointer' }}>{avd}</label>
                  </div>
                ))}
              </div>

              <div className="info-box info-box-blue">
                ℹ️ Systemet beräknar timmar automatiskt och tar hänsyn till detta vid Excel-import och X-dagsjusteringar.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setVisaModal(false)}>Avbryt</button>
              <button className="btn btn-primary" onClick={handleSpara} disabled={sparar}>
                {sparar ? 'Sparar...' : redigerar ? 'Spara ändringar' : 'Spara passtyp'}
              </button>
            </div>
          </div>
        </div>
      )}

      {taBortDialog && (
        <BekraftaModal
          titel="Ta bort passtyp"
          meddelande={`Ta bort passtypen "${taBortDialog.namn}"? Åtgärden kan inte ångras.`}
          farligKnapp
          onBekrafta={bekraftaTaBort}
          onAvbryt={() => setTaBortDialog(null)}
        />
      )}
    </div>
  )
}
